/**
 * GuardianHomeScreen — landing page for a parent or legal guardian.
 *
 * Under-15 students cannot hold an independent UAE PASS identity, so the login
 * account belongs to their guardian. This lists the children that account is
 * responsible for and routes into the existing read-only student views.
 */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsive } from '../../hooks/useResponsive';
import { createShadow } from '../../utils/styles';
import { MainStackParamList } from '../../navigation/MainNavigator';
import api from '../../services/api';

type NavProp = NativeStackNavigationProp<MainStackParamList>;

const FOREGROUND = '#414042';
const BORDER = '#E2E8F0';
const MUTED = '#64748B';
const BRAND_RED = '#A9211F';

interface Child {
  id: number;
  name: string;
  school?: string;
  status?: string;
  specialization?: string;
}

const STATUS_LABELS: Record<string, { label: string; bg: string; fg: string }> = {
  applied: { label: 'بإنتظار الموافقة', bg: '#FFEDD5', fg: '#9A3412' },
  proposed: { label: 'بإنتظار الاعتماد', bg: '#DBEAFE', fg: '#1E40AF' },
  active: { label: 'نشط', bg: '#DCFCE7', fg: '#166534' },
  rejected: { label: 'مرفوض', bg: '#FEE2E2', fg: '#991B1B' },
};

export default function GuardianHomeScreen() {
  const navigation = useNavigation<NavProp>();
  const { language } = useLanguage();
  const { colors } = useTheme();
  const { isMobile } = useResponsive();
  const isRTL = language === 'ar';

  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/talent-students/my-children/');
        if (!cancelled) setChildren(response.data || []);
      } catch (e) {
        console.log('[GuardianHome] failed to load children:', e);
        if (!cancelled) {
          setError(isRTL ? 'تعذر تحميل بيانات الأبناء' : 'Could not load children');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isRTL]);

  return (
    <PageContainer>
      <Navbar />
      <ScrollView contentContainerStyle={{ padding: isMobile ? 16 : 24 }}>
        <Breadcrumb items={[{ label: isRTL ? 'أبنائي' : 'My children' }]} />

        <Text style={[styles.title, { color: colors.textPrimary }]}>
          {isRTL ? 'أبنائي' : 'My children'}
        </Text>

        {loading && <ActivityIndicator size="large" color={BRAND_RED} />}

        {/* Registering another *child* is a different action from registering
            another talent for an existing child. The public form resolves the
            guardian by Emirates ID, so re-entering it cannot duplicate them. */}
        {!loading && error === '' && (
          <View style={styles.topActions}>
            <TouchableOpacity
              style={styles.addChildButton}
              activeOpacity={0.85}
              onPress={() => {
                // The public form is mounted by App.tsx on a URL path, not as a
                // navigator route, so this is a real page navigation.
                if (Platform.OS === 'web') window.location.href = '/register';
              }}
            >
              <Text style={styles.addChildButtonText}>
                {isRTL ? 'تسجيل ابن آخر' : 'Register another child'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.addChildButton}
              activeOpacity={0.85}
              onPress={() => navigation.navigate('GuardianEdit', { mode: 'self' })}
            >
              <Text style={styles.addChildButtonText}>
                {isRTL ? 'تعديل بياناتي' : 'Edit my details'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {!loading && error !== '' && <Text style={styles.error}>{error}</Text>}

        {!loading && error === '' && children.length === 0 && (
          <Text style={styles.empty}>
            {isRTL
              ? 'لا يوجد أبناء مرتبطون بحسابك حتى الآن'
              : 'No children are linked to your account yet'}
          </Text>
        )}

        {!loading && children.map((child) => {
          const badge = STATUS_LABELS[child.status || ''] || null;
          return (
            <TouchableOpacity
              key={child.id}
              style={[styles.card, { backgroundColor: colors.card }, createShadow(2)]}
              activeOpacity={0.85}
              onPress={() =>
                navigation.navigate('StudentDetail', { id: child.id, name: child.name })
              }
            >
              <View style={styles.cardHeader}>
                <Text style={[styles.childName, { color: colors.textPrimary }]}>
                  {child.name}
                </Text>
                {badge && (
                  <Text style={[styles.badge, { backgroundColor: badge.bg, color: badge.fg }]}>
                    {badge.label}
                  </Text>
                )}
              </View>

              {!!child.school && <Text style={styles.childMeta}>{child.school}</Text>}
              {!!child.specialization && (
                <Text style={styles.childMeta}>{child.specialization}</Text>
              )}

              <View style={styles.cardActions}>
                <TouchableOpacity
                  style={styles.registerButton}
                  activeOpacity={0.85}
                  onPress={() =>
                    navigation.navigate('StudentTalentRegister', { studentId: child.id })
                  }
                >
                  <Text style={styles.registerButtonText}>
                    {isRTL ? 'تسجيل موهبة' : 'Register a talent'}
                  </Text>
                </TouchableOpacity>

                {/* The backend refuses edits once the registration is approved,
                    so the button is only offered while it is still pending. */}
                {['applied', 'proposed'].includes(child.status || '') && (
                  <TouchableOpacity
                    style={styles.registerButton}
                    activeOpacity={0.85}
                    onPress={() =>
                      navigation.navigate('GuardianEdit', { mode: 'child', studentId: child.id })
                    }
                  >
                    <Text style={styles.registerButtonText}>
                      {isRTL ? 'تعديل البيانات' : 'Edit details'}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  title: {
    fontFamily: FONTS.medium,
    fontSize: 22,
    textAlign: 'right',
    marginBottom: 16,
  },
  empty: {
    fontFamily: FONTS.regular,
    fontSize: 15,
    color: MUTED,
    textAlign: 'center',
    marginTop: 40,
  },
  error: {
    fontFamily: FONTS.regular,
    fontSize: 15,
    color: BRAND_RED,
    textAlign: 'center',
    marginTop: 24,
  },
  card: {
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  childName: {
    fontFamily: FONTS.medium,
    fontSize: 17,
  },
  childMeta: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: MUTED,
    textAlign: 'right',
    marginTop: 4,
  },
  badge: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden',
  },
  topActions: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  cardActions: {
    flexDirection: 'row',
    gap: 12,
  },
  registerButton: {
    flex: 1,
    marginTop: 12,
    borderWidth: 1,
    borderColor: BRAND_RED,
    borderRadius: 8,
    paddingVertical: 8,
    alignItems: 'center',
  },
  registerButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: BRAND_RED,
  },
  addChildButton: {
    flex: 1,
    borderWidth: 1,
    borderColor: BRAND_RED,
    borderStyle: 'dashed',
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
    marginBottom: 16,
  },
  addChildButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: BRAND_RED,
  },
});
